/**
 * Tutorial Setup Functions — per-step state manipulation.
 *
 * Called when the tutorial controller advances to a new step.
 * Sets tank positions, damage values, wind, forces tumble for recovery demo, etc.
 */

import type { GameState } from '../types';
import { getGroundY, isPointInTerrain } from '../terrain/grid';
import { PHYSICS, TUTORIAL } from '../constants';
import { mirrorSettle } from '../physics/transitions';
import { TUTORIAL_STEPS } from './steps';
import { getDefaultWeapon, createProjectile } from '../weapons';

/** Reset opponent to a standing position at given X offset from stage center. */
function resetOpponent(state: GameState, xOffset: number, damage: number): void {
  const dummy = state.players[1];
  if (!dummy) return;
  const centerX = (state.stageLeft + state.stageRight) / 2;
  dummy.x = centerX + xOffset;
  dummy.y = getGroundY(dummy.x) - PHYSICS.ECB_RADIUS;
  dummy.vx = 0;
  dummy.vy = 0;
  dummy.tumble = false;
  dummy.hitstunFrames = 0;
  dummy.settled = true;
  dummy.settledX = dummy.x;
  dummy.settledY = dummy.y;
  mirrorSettle(dummy);
  dummy.damagePercent = damage;
  dummy.alive = true;
  dummy.pendingDeath = false;
  // Trigger fade-in from invisible (cosmetic reposition transition)
  state.modeState.tutorialOpponentFade = 0;
  // Update dummyX so respawn uses this position
  state.modeState.dummyX = dummy.x;
}

/** Reset the player to a standing position at stage center. */
function resetPlayer(state: GameState): void {
  const player = state.players[0];
  if (!player) return;
  const centerX = (state.stageLeft + state.stageRight) / 2;
  player.x = centerX;
  player.y = getGroundY(centerX) - PHYSICS.ECB_RADIUS;
  player.vx = 0;
  player.vy = 0;
  player.tumble = false;
  player.hitstunFrames = 0;
  player.grounded = true;
  player.settled = true;
  player.settledX = player.x;
  player.settledY = player.y;
  mirrorSettle(player);
}

/** Run setup for the given tutorial step. */
export function setupTutorialStep(state: GameState, stepIndex: number): void {
  const step = TUTORIAL_STEPS[stepIndex];
  if (!step) return;

  // Wind override
  if (step.windOverride !== undefined && step.windOverride !== null) {
    state.wind = step.windOverride;
  }

  // Step-specific setup
  switch (step.id) {
    case 'move_jump':
      resetOpponent(state, 400, 0);
      break;

    case 'aim_fire':
      resetOpponent(state, 350, 0);
      break;

    case 'hit_edgeguard':
      // Opponent at +200px with 80% damage — guarantees strong knockback into edgeguard
      resetOpponent(state, 200, step.opponentDamage ?? TUTORIAL.HIT_DEMO_DAMAGE);
      break;

    case 'wind_shot':
      resetPlayer(state);
      resetOpponent(state, 300, 0);
      state.currentPlayer = 0;
      break;

    case 'energy_tier':
      resetPlayer(state);
      resetOpponent(state, 300, 0);
      state.currentPlayer = 0;
      break;

    case 'kill': {
      // Reset both players. Opponent at +250px, 120% damage.
      resetPlayer(state);
      state.players[0].damagePercent = 0;
      resetOpponent(state, 250, step.opponentDamage ?? TUTORIAL.KILL_DAMAGE);
      state.currentPlayer = 0;
      break;
    }

    case 'incoming_hit': {
      // Scene 1: opponent fires at player. Controller handles projectile after delay.
      resetPlayer(state);
      state.players[0].damagePercent = TUTORIAL.INCOMING_PLAYER_DAMAGE;
      resetOpponent(state, 400, 0);
      // Opponent's turn so hit detection treats player 0 as defender
      state.currentPlayer = 1;
      // Strip edgeguard so dummy doesn't get a follow-up beam on this step
      const dummyIncoming = state.players[1];
      if (dummyIncoming) {
        const baseWeapon = getDefaultWeapon();
        dummyIncoming.selectedWeapon = { ...baseWeapon, edgeguard: undefined };
      }
      break;
    }

    case 'di_basics':
      // Continues from incoming_hit — player mid-knockback, DI window opening.
      // Controller handles freeze. No position reset.
      break;

    case 'di_strategic': {
      // Scene 3: fresh hit with edgeguard enabled. Reset positions.
      resetPlayer(state);
      state.players[0].damagePercent = TUTORIAL.STRATEGIC_DI_DAMAGE;
      resetOpponent(state, 300, 0);
      state.currentPlayer = 1;
      // KEEP edgeguard config so opponent gets railgun follow-up
      const dummyStrategic = state.players[1];
      if (dummyStrategic) {
        const baseWeapon = getDefaultWeapon();
        dummyStrategic.selectedWeapon = { ...baseWeapon }; // edgeguard intact
      }
      break;
    }

    case 'recovery':
      // Scene 4: continues from di_strategic — player airborne after surviving edgeguard.
      // No position reset. Controller handles freeze.
      break;

    case 'burst_escape': {
      // Scene 5: player falling near void, must burst back to stage.
      // Position relative to voidY (guaranteed open air below terrain).
      const centerX = (state.stageLeft + state.stageRight) / 2;
      const player = state.players[0];
      let spawnY = state.voidY - TUTORIAL.BURST_ESCAPE_VOID_MARGIN;
      // Safety: if somehow inside terrain, scan downward to open air
      if (isPointInTerrain(centerX, spawnY)) {
        for (let probe = spawnY + 10; probe < state.voidY - PHYSICS.ECB_RADIUS; probe += 10) {
          if (!isPointInTerrain(centerX, probe)) {
            spawnY = probe;
            break;
          }
        }
      }
      player.x = centerX;
      player.y = spawnY;
      player.vx = 0;
      player.vy = 0;
      player.tumble = false;
      player.hitstunFrames = 0;
      player.grounded = false;
      player.settled = false;
      player.damagePercent = 0;
      player.specialUsed = false;
      resetOpponent(state, 350, 0);
      state.currentPlayer = 0;
      break;
    }

    case 'win_battle': {
      // Scene 6: clean fight. Player's turn, opponent pre-damaged.
      resetPlayer(state);
      state.players[0].damagePercent = 0;
      resetOpponent(state, 350, step.opponentDamage ?? TUTORIAL.KILL_DAMAGE);
      state.currentPlayer = 0;
      break;
    }
  }

  // Apply player charge override
  if (step.playerCharge !== undefined) {
    state.players[0].weaponCharge = step.playerCharge;
  }

  // Accumulate unlocked keys
  const ms = state.modeState;
  for (const key of step.unlockKeys) {
    if (!ms.tutorialUnlockedKeys.includes(key)) {
      ms.tutorialUnlockedKeys.push(key);
    }
  }
}

/**
 * Fire a projectile from the opponent toward the player with a visible arc.
 *
 * Spawns from the opponent's position (like normal fire()) and solves for
 * a launch angle that accounts for mortar gravity. Produces a ~30-50 frame
 * flight with a natural arc the player can see coming.
 *
 * @param keepEdgeguard - if true, weapon keeps edgeguard config for railgun follow-up
 */
export function fireOpponentShot(state: GameState, keepEdgeguard = false): void {
  const opponent = state.players[1];
  const player = state.players[0];
  if (!opponent || !player) return;

  const baseWeapon = getDefaultWeapon();
  const weapon = keepEdgeguard
    ? { ...baseWeapon }
    : { ...baseWeapon, edgeguard: undefined };

  // Spawn offset from opponent center (same formula as aiming.ts:fire())
  const spawnOffset = PHYSICS.ECB_RADIUS + weapon.projectileRadius + 2;

  // Target displacement from opponent
  const dx = player.x - opponent.x;
  const dy = player.y - opponent.y;
  const g = weapon.gravity;

  // Dynamic speed: ensure enough range for the actual distance, with 20% margin.
  // At 45° (optimal), max range = v²/g. So min v = sqrt(g * |dx|).
  const baseSpeed = 0.5 * PHYSICS.MAX_POWER * weapon.projectileSpeed;
  const minSpeed = Math.sqrt(g * Math.abs(dx)) * 1.2;
  const speed = Math.max(minSpeed, baseSpeed);

  // Solve for launch angle accounting for gravity.
  // Screen coords: +y is down, gravity adds to vy each frame.
  // Projectile motion: dx = v*cos(a)*t, dy = -v*sin(a)*t + 0.5*g*t^2
  // From t = dx/(v*cos(a)), substitute and solve for angle a.
  const angle = solveArcAngle(dx, dy, speed, g);

  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const spawnX = opponent.x + cosA * spawnOffset;
  const spawnY = opponent.y - sinA * spawnOffset;

  const vx = cosA * speed;
  const vy = -sinA * speed;

  const proj = createProjectile(
    weapon,
    spawnX, spawnY,
    vx, vy,
    1, // firedBy opponent (player index 1)
    state.frameCount,
  );
  state.projectiles.push(proj);
}

/**
 * Solve for launch angle to hit a target at (dx, dy) with given speed and gravity.
 * Uses the standard projectile trajectory formula. Returns the low-arc solution.
 * Falls back to direct aim if no arc solution exists (target too far).
 */
function solveArcAngle(dx: number, dy: number, speed: number, g: number): number {
  const absDx = Math.abs(dx);
  const s2 = speed * speed;
  const s4 = s2 * s2;

  // Discriminant for the trajectory equation:
  // tan(a) = (v^2 +/- sqrt(v^4 - g*(g*dx^2 + 2*dy*v^2))) / (g*dx)
  // Note: dy is positive-down in screen coords, but the formula uses
  // physics-up convention, so we negate dy.
  const physDy = -dy; // convert to physics convention (positive = up)
  const disc = s4 - g * (g * absDx * absDx + 2 * physDy * s2);

  if (disc < 0 || absDx < 1) {
    // No valid arc (too far or too close) — aim directly
    return Math.atan2(-dy, dx);
  }

  // Low-arc solution (minus sqrt) gives a flatter, faster trajectory
  const tanA = (s2 - Math.sqrt(disc)) / (g * absDx);
  const baseAngle = Math.atan(tanA);

  // Flip angle if target is to the left
  return dx < 0 ? Math.PI - baseAngle : baseAngle;
}
